/**
 * Presentación. Nada de aritmética de dinero aquí: el backend manda centavos
 * enteros y esto solo los pinta (specs/001-contrato-datos.md).
 */

const PESOS = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

const PESOS_EXACTOS = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  minimumFractionDigits: 2,
});

/**
 * Los centavos se ocultan cuando son cero, que en pesos colombianos es casi
 * siempre. Cuando no lo son, se muestran: una diferencia de $0,37 puede ser toda
 * la historia de una comisión bancaria.
 */
export function pesos(centavos: number | null | undefined): string {
  if (centavos === null || centavos === undefined || Number.isNaN(centavos)) return "—";
  const valor = centavos / 100;
  return Number.isInteger(valor) ? PESOS.format(valor) : PESOS_EXACTOS.format(valor);
}

/** Para titulares: "$1,2 M". El valor exacto va siempre en el `title`. */
export function pesosCortos(centavos: number | null | undefined): string {
  if (centavos === null || centavos === undefined) return "—";
  const valor = Math.abs(centavos / 100);
  if (valor >= 1_000_000_000) return `$${(centavos / 100_000_000_000).toFixed(1)} MM`;
  if (valor >= 1_000_000) return `$${(centavos / 100_000_000).toFixed(1)} M`;
  if (valor >= 10_000) return `$${Math.round(centavos / 100_000)} K`;
  return pesos(centavos);
}

const FECHA_CORTA = new Intl.DateTimeFormat("es-CO", { day: "2-digit", month: "short" });
const FECHA_LARGA = new Intl.DateTimeFormat("es-CO", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});
const FECHA_HORA = new Intl.DateTimeFormat("es-CO", {
  dateStyle: "medium",
  timeStyle: "short",
});
const MES_LARGO = new Intl.DateTimeFormat("es-CO", { month: "long", year: "numeric" });

/**
 * "2026-09" → "septiembre de 2026".
 *
 * Se ancla a mediodía UTC porque un `new Date("2026-09-01")` en Bogotá cae en
 * el 31 de agosto, y la serie enseñaría el mes de al lado.
 */
export function mesLargo(mes: string): string {
  const partes = /^(\d{4})-(\d{2})$/.exec(mes);
  // Un mes que no sabemos leer se enseña crudo. Inventar uno sería peor: la
  // serie existe para decidir sobre D-56, y un mes equivocado decide mal.
  if (!partes) return mes;
  return MES_LARGO.format(new Date(Date.UTC(Number(partes[1]), Number(partes[2]) - 1, 1, 12)));
}

/** "2026-09-02" — una fecha de calendario, sin hora ni zona. */
const SOLO_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;
/** "2026-09-02T00:00:00.000Z" — una fecha de calendario que viajó como instante. */
const MEDIANOCHE_UTC = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?Z$/;

/**
 * El instante de un valor, en milisegundos, para ORDENAR (no para mostrar).
 *
 * Devuelve 0 cuando no hay fecha, que en un orden descendente manda lo que no
 * tiene fecha al final: un movimiento sin fecha no es «el más nuevo».
 *
 * Existe porque `aFecha` no se exporta a propósito —parsear fechas es de este
 * módulo, y repartir esa lógica es cómo se pierde la corrección del día— pero
 * ordenar sí es de quien lista.
 */
export const instanteDe = (v: string | Date | null | undefined) => aFecha(v)?.getTime() ?? 0;

function aFecha(valor: string | Date | null | undefined): Date | null {
  if (!valor) return null;

  // Las columnas `date` de Postgres llegan como "2026-09-02", y `new Date()`
  // interpreta ESO como medianoche UTC. En Bogotá (UTC-5) eso es el día
  // anterior a las 19:00, así que el panel mostraba todas las fechas un día
  // antes: un pago del 2 aparecía como del 1.
  //
  // No es un detalle cosmético en un sistema que cuadra plata: el contador
  // compara la fecha del banco con la de su registro, y no cuadraban por un
  // día sin ninguna razón visible.
  //
  // Se construye como fecha LOCAL: el 2 de septiembre es el 2 de septiembre,
  // no un instante que dependa de dónde se mire.
  if (typeof valor === "string") {
    const m = SOLO_FECHA.exec(valor);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));

    // El libro no manda "2026-09-02" sino "2026-09-02T00:00:00.000Z": el
    // backend lee la columna `date` como un Date de JS y lo serializa como
    // instante. Medianoche UTC exacta NO es un pago a medianoche — es una fecha
    // de calendario que perdió su naturaleza al viajar.
    //
    // La heurística es segura mientras esas columnas sean `date`: la hora se
    // descarta al guardar, así que un movimiento nunca lleva una hora real.
    // Si algún día pasan a `timestamptz`, esto hay que quitarlo — y entonces lo
    // correcto sería que el backend distinga fecha de instante al serializar,
    // que es donde de verdad se pierde el dato.
    const iso = MEDIANOCHE_UTC.exec(valor);
    if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }

  const d = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const fechaCorta = (v: string | Date | null | undefined) => {
  const d = aFecha(v);
  return d ? FECHA_CORTA.format(d) : "—";
};

export const fechaLarga = (v: string | Date | null | undefined) => {
  const d = aFecha(v);
  return d ? FECHA_LARGA.format(d) : "—";
};

/**
 * ¿Este valor trae una hora de verdad?
 *
 * Una columna `date` llega como "2026-09-02", y el libro la manda como
 * medianoche UTC exacta — las dos son fechas de calendario, no instantes. La
 * hora del banco viaja como "2026-08-13T15:22:00", sin `Z`, precisamente para
 * poder distinguirla de eso.
 */
const traeHora = (v: string | Date | null | undefined): boolean =>
  typeof v === "string" && !SOLO_FECHA.test(v) && !MEDIANOCHE_UTC.test(v) && /T\d{2}:/.test(v);

const HORA = new Intl.DateTimeFormat("es-CO", { hour: "numeric", minute: "2-digit" });

/**
 * La fecha con su hora cuando la hay, y solo la fecha cuando no.
 *
 * El banco escribe la hora en su alerta ("el 13/08/2026 a las 15:22") y hasta la
 * migración 002 se tiraba al guardar. Es lo que distingue dos pagos del mismo
 * cliente por el mismo monto el mismo día — el par que no se puede confundir.
 * Lo cargado antes de esa migración no la tiene, y entonces no se inventa.
 */
export const fechaConHora = (v: string | Date | null | undefined) => {
  const d = aFecha(v);
  if (!d) return "—";
  return traeHora(v) ? `${FECHA_LARGA.format(d)}, ${HORA.format(d)}` : FECHA_LARGA.format(d);
};

/** Solo la hora, o cadena vacía si el dato no la trae. */
export const soloHora = (v: string | Date | null | undefined) => {
  const d = aFecha(v);
  return d && traeHora(v) ? HORA.format(d) : "";
};

export const fechaHora = (v: string | Date | null | undefined) => {
  const d = aFecha(v);
  return d ? FECHA_HORA.format(d) : "—";
};

const RELATIVO = new Intl.RelativeTimeFormat("es-CO", { numeric: "auto" });
const ESCALAS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 31_536_000_000],
  ["month", 2_592_000_000],
  ["day", 86_400_000],
  ["hour", 3_600_000],
  ["minute", 60_000],
];

/** "hace 3 días". Más humano que una fecha para todo lo reciente. */
export function haceRato(v: string | Date | null | undefined): string {
  const d = aFecha(v);
  if (!d) return "—";
  const dif = d.getTime() - Date.now();
  for (const [unidad, ms] of ESCALAS) {
    if (Math.abs(dif) >= ms) return RELATIVO.format(Math.round(dif / ms), unidad);
  }
  return "hace un momento";
}

/** Minutos → "2 h 15 min". Para las métricas de reacción. */
export function duracion(minutos: number | null | undefined): string {
  if (minutos === null || minutos === undefined) return "—";
  if (minutos < 1) return "menos de un minuto";
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas < 24) return resto ? `${horas} h ${resto} min` : `${horas} h`;
  const dias = Math.floor(horas / 24);
  return `${dias} d ${horas % 24} h`;
}

export const porcentaje = (fraccion: number | null | undefined, decimales = 0): string =>
  fraccion === null || fraccion === undefined ? "—" : `${(fraccion * 100).toFixed(decimales)} %`;

/** Plural sin sorpresas: `plural(1, "pago", "pagos")`. */
export const plural = (n: number, uno: string, varios: string) => (n === 1 ? uno : varios);

export const inicial = (texto: string | undefined) =>
  (texto ?? "?").trim().charAt(0).toUpperCase() || "?";

/**
 * El monto que alguien busca, en centavos, o null si lo buscado no es un monto
 * (SPEC-029 §8).
 *
 * «41000», «41.000», «$41.000», «41,000» y «41.000,00» son lo mismo para quien
 * teclea en el mostrador. Antes solo «41.000» encontraba algo, y por casualidad:
 * aparecía escrito así en la razón del pago.
 */
export function montoBuscado(q: string): number | null {
  const t = String(q ?? "").trim().replace(/^\$\s*/, "");
  if (!/^[\d.,\s]+$/.test(t) || !/\d/.test(t)) return null;
  // Una coma o un punto seguidos de exactamente dos cifras al final son
  // centavos: «41.000,00». Cualquier otro separador es de miles.
  const sinCentavos = t.replace(/[.,](\d{2})$/, (_, c) => (c === "00" ? "" : `#${c}`));
  const [enteros, centavos = "00"] = sinCentavos.split("#");
  const pesosEnteros = Number((enteros ?? "").replace(/\D/g, ""));
  if (!Number.isFinite(pesosEnteros)) return null;
  return pesosEnteros * 100 + Number(centavos);
}

/** ¿Lo buscado es este monto? */
export const esElMontoBuscado = (q: string, centavos: number | null | undefined) => {
  const buscado = montoBuscado(q);
  return buscado !== null && centavos !== null && centavos !== undefined && buscado === centavos;
};
